import React from 'react';
import { Box } from '@mui/material';
import type { SxProps, Theme } from '@mui/material';
import { sbg, StatusTone } from '../../theme/sbgTokens';

export type { StatusTone };

export interface StatusPillProps extends Omit<React.HTMLAttributes<HTMLSpanElement>, 'title' | 'children'> {
  tone: StatusTone;
  children: React.ReactNode;
  /** Replace the 6px dot with a 12px icon. */
  icon?: React.ReactNode;
  /** Hide the leading dot (rare: dense tags where the word stands alone). */
  noDot?: boolean;
  /** Comfort size (24px). Compact screens use the default 22px. */
  size?: 'compact' | 'comfort';
  title?: string;
  sx?: SxProps<Theme>;
}

/**
 * Status pill (book §11.11): a label, not a control — no hover, no pointer.
 * Color always travels with a word. People and ops mapping for EMS:
 * neutral = Off · Not scheduled; info = Scheduled · WFH · In review;
 * warning = Late · Pending approval; success = Present · Approved;
 * danger = Absent (unexcused) · Denied. Funded (Volt) is for money moments only.
 */
const StatusPill = React.forwardRef<HTMLSpanElement, StatusPillProps>(function StatusPill(
  { tone, children, icon, noDot = false, size = 'compact', title, sx, ...rest },
  ref,
) {
  const colors = sbg.status[tone];
  return (
    <Box
      ref={ref}
      component="span"
      title={title}
      {...rest}
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 0.75,
        height: size === 'comfort' ? 24 : 22,
        px: 1,
        borderRadius: sbg.radius.pill,
        border: `1px solid ${colors.border}`,
        bgcolor: colors.bg,
        color: colors.text,
        font: 'var(--sbg-type-label-xs)',
        whiteSpace: 'nowrap',
        verticalAlign: 'middle',
        fontVariantNumeric: 'tabular-nums',
        flexShrink: 0,
        '& > svg': { width: 12, height: 12, flex: 'none' },
        ...sx,
      }}
    >
      {icon ?? (!noDot && <Box component="span" aria-hidden="true" sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: 'currentColor', flex: 'none' }} />)}
      {children}
    </Box>
  );
});

export default StatusPill;

/**
 * Count badge (book §11.11): neutral for nav and tab counts; `alert` (Blue)
 * for notifications; `tone` (danger/warning) only when the count reflects a
 * true bad state. `label` is read to screen readers instead of the bare number.
 */
export const CountBadge: React.FC<{
  count: number;
  alert?: boolean;
  tone?: 'neutral' | 'warning' | 'danger';
  max?: number;
  /** Screen-reader text, e.g. "3 critical issues". */
  label?: string;
  'aria-label'?: string;
  sx?: SxProps<Theme>;
}> = ({ count, alert = false, tone = 'neutral', max = 99, label, sx, ...rest }) => (
  <Box
    component="span"
    sx={{
      display: 'inline-grid',
      placeItems: 'center',
      minWidth: 18,
      height: 18,
      px: '5px',
      borderRadius: sbg.radius.pill,
      font: 'var(--sbg-type-label-xs)',
      fontVariantNumeric: 'tabular-nums',
      ...(alert
        ? { bgcolor: sbg.action.primary, color: sbg.text.onBrand }
        : { bgcolor: sbg.status[tone].bg, color: sbg.status[tone].text, boxShadow: `inset 0 0 0 1px ${sbg.status[tone].border}` }),
      ...sx,
    }}
  >
    <span aria-hidden={label || rest['aria-label'] ? true : undefined}>{count > max ? `${max}+` : count}</span>
    {(label || rest['aria-label']) && <span className="sbg-sr-only">{label ?? rest['aria-label']}</span>}
  </Box>
);

/** Category tag (book §11.11): uppercase overline in a hairline pill. For types and labels, never status. */
export const Tag: React.FC<{ children: React.ReactNode; sx?: SxProps<Theme> }> = ({ children, sx }) => (
  <Box
    component="span"
    sx={{
      display: 'inline-flex',
      alignItems: 'center',
      height: 22,
      px: 1.25,
      borderRadius: sbg.radius.pill,
      border: `1px solid ${sbg.border.default}`,
      font: 'var(--sbg-type-overline)',
      letterSpacing: 'var(--sbg-type-overline-tracking)',
      textTransform: 'uppercase',
      color: sbg.text.primary,
      whiteSpace: 'nowrap',
      ...sx,
    }}
  >
    {children}
  </Box>
);
